import React from 'react';

import slugify from 'slugify';

import { Link, styled } from '@mui/material';

import { cssVars, TextSelect } from '@mewa/ui';

import { Section } from '../../dynamic-content-page/fragment-and-types';

export type ContentSection = {
  heading: string;
  id: string;
};

export const getSectionId = (heading: string) =>
  // Dots "." are not allowed in html element IDs when using .querySelector()
  slugify(heading.replace('.', ''), {
    lower: true,
    strict: false,
    trim: true,
  });

const scrollToSection = (sectionId: string, isMobile: boolean) => {
  const HEADER_OFFSET =
    (isMobile
      ? parseInt(cssVars.headerHeightMobile) + 64 // 64px = height of table of contents mobile
      : parseInt(cssVars.headerHeightDesktop)) + 20; // 20px margin top

  const top = document
    .querySelector(`#${sectionId}`)
    ?.getBoundingClientRect().top;

  window.scrollTo({
    top: window.scrollY + (top ?? 0) - HEADER_OFFSET,
    behavior: 'smooth',
  });
};

export const TableOfContents = ({
  sections,
  isMobile = false,
  activeSection,
}: {
  sections: Section[];
  isMobile?: boolean;
  activeSection: number;
}) => {
  const sectionsWithId = sections.filter(
    (section): section is Required<Section> =>
      !!section.id && !!section.heading,
  );
  const activeIndex = getIndexForSectionsWithId(activeSection, sections);

  return sectionsWithId.length > 0 ? (
    isMobile ? (
      <ContainerMobile>
        <TextSelect
          id={'sort-articles'}
          label={'Inhalt'}
          activeIndex={activeIndex}
          onChange={(value) => {
            scrollToSection(sectionsWithId[value].id, true);
          }}
          items={sectionsWithId.map((section) => ({
            name: section.heading,
          }))}
        />
      </ContainerMobile>
    ) : (
      <ContainerDesktop>
        <HeadingTableOfContent>Inhalt</HeadingTableOfContent>
        <ContentList>
          {sectionsWithId.map((section, idx) => (
            <Item key={idx}>
              <ContentListItemLink
                color={idx === activeIndex ? 'primary' : 'secondary'}
                underline="hover"
                onClick={() => {
                  scrollToSection(section.id, false);
                }}
              >
                {section.heading}
              </ContentListItemLink>
            </Item>
          ))}
        </ContentList>
      </ContainerDesktop>
    )
  ) : null;
};

const getIndexForSectionsWithId = (
  activeSection: number,
  sections: { id?: string }[],
) => sections.slice(0, activeSection + 1).filter((s) => s.id).length - 1;

const ContentListItemLink = styled(Link)(({ theme }) => ({
  [theme.breakpoints.up('sm')]: {
    ...theme.typography.body,
  },
}));

const ContainerDesktop = styled('div')(({ theme }) => ({
  zIndex: 2,
  backgroundColor: 'var(--color-white)',
  marginTop: '40px',

  [theme.breakpoints.up('md')]: {
    marginTop: '50px',
  },

  [theme.breakpoints.up('lg')]: {
    marginTop: '60px',
  },
}));

const ContainerMobile = styled('div')(({ theme }) => ({
  padding: '15px var(--margin) 0 var(--margin)',
  margin: '0 calc(-1 * var(--margin))',
  backgroundColor: 'var(--color-white)',

  [theme.breakpoints.down('sm')]: {
    '.sticky &': {
      boxShadow: '0px 0px 24px 4px rgba(0, 0, 0, 0.17)',

      '& .native-dropdown': {
        border: 'none',
      },
    },
  },
}));

const ContentList = styled('ul')({
  listStyle: 'none',
  paddingLeft: 0,
});

const HeadingTableOfContent = styled('h2')(({ theme }) => ({
  ...theme.typography.h4,

  marginTop: 0,
  marginBottom: 0,
}));

const Item = styled('li')(({ theme }) => ({
  [theme.breakpoints.up('sm')]: {
    marginTop: '15px',
  },

  [theme.breakpoints.up('md')]: {
    marginTop: '20px',
  },

  [theme.breakpoints.up('lg')]: {
    marginTop: '30px',
  },
}));
